import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import type { Bot } from '../utils/botHelpers';
import type { User } from 'firebase/auth';

// Chrome-level state shared by the Sidebar and every routed dashboard page:
// the bot list, account/user info, and the Settings-modal open/close signal.
// A real React Context (not react-router's useOutletContext) because the
// Sidebar is a SIBLING of <Outlet/> inside DashboardLayout, not a descendant
// of it — outlet context only reaches descendants of the matched route
// element, so the Sidebar's gear icon (which needs to open Settings for
// whichever bot is deep inside the routed subtree) needs this instead.
interface DashboardChromeContextValue {
  bots: Bot[];
  loading: boolean;
  currentUser: User | null;
  userData: any;
  hasOverdueInvoice: boolean;
  serverVersion: string;
  refreshBots: () => Promise<void>;
  settingsBotId: string | null;
  openSettings: (botId: string) => void;
  closeSettings: () => void;
}

const DashboardChromeContext = createContext<DashboardChromeContextValue | null>(null);

export function DashboardChromeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [hasOverdueInvoice, setHasOverdueInvoice] = useState(false);
  const [serverVersion, setServerVersion] = useState('');
  const [settingsBotId, setSettingsBotId] = useState<string | null>(null);

  const refreshBots = useCallback(async () => {
    if (!auth.currentUser) { setLoading(false); return; }
    try {
      const q = query(ref(db, 'bots'), orderByChild('userId'), equalTo(auth.currentUser.uid));
      const snap = await get(q);
      const list: Bot[] = [];
      snap.forEach((c) => {
        const bot: any = { id: c.key!, ...c.val() };
        if (!bot.botType) {
          if (bot.phoneNumberId || bot.accessToken) bot.botType = 'whatsapp';
          else if (bot.fbPageId) bot.botType = 'facebook';
          else if (bot.websiteUrl) bot.botType = 'website';
          else bot.botType = 'whatsapp';
        }
        list.push(bot);
      });
      setBots(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const [userSnap, invSnap] = await Promise.all([
          get(ref(db, `users/${user.uid}`)),
          get(ref(db, `invoices/${user.uid}`)),
        ]);
        const ud = userSnap.val();
        setUserData(ud);
        if (!ud?.onboardingCompleted) { navigate('/onboarding'); return; }

        if (invSnap.exists()) {
          let overdue = false;
          invSnap.forEach(inv => { if (inv.val()?.status === 'overdue') overdue = true; });
          setHasOverdueInvoice(overdue);
        }

        refreshBots();
      } else {
        navigate('/login');
      }
    });
    return unsub;
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    apiFetch('/version').then(r => r.json()).then(d => setServerVersion(d.version)).catch(() => {});
  }, []);

  const value: DashboardChromeContextValue = {
    bots, loading, currentUser, userData, hasOverdueInvoice, serverVersion, refreshBots,
    settingsBotId, openSettings: setSettingsBotId, closeSettings: () => setSettingsBotId(null),
  };

  return <DashboardChromeContext.Provider value={value}>{children}</DashboardChromeContext.Provider>;
}

export function useDashboardChrome(): DashboardChromeContextValue {
  const ctx = useContext(DashboardChromeContext);
  if (!ctx) throw new Error('useDashboardChrome must be used within a DashboardChromeProvider');
  return ctx;
}
